/**
 * A section of the Data Egress management surface (ADR-024 §4; Flow W).
 *
 * Deliberately the same treatment as the System Status detail pane's
 * :func:`@/system-status/status-detail-primitives.DetailSection` — one
 * consolidated scrollable surface with **sticky section headers and no
 * sub-tabs**, because the management surface is opened from that pane and an
 * operator should not have to re-learn the shape between reading a flow's status
 * and editing its configuration.
 *
 * It is a separate component rather than a reuse only because this one carries
 * an optional header ``action`` slot (the "Add connection" affordance sits in
 * its section header), and pushing that slot into the read-only status
 * primitive would put a mutation seam in a component whose whole job is display.
 *
 * # The ``help`` slot
 *
 * Added 2026-10-10 (architect direction). The provider and peer editors had
 * accumulated standing paragraphs explaining how their controls behave — what
 * stopping suppresses, what an export omits, when start/stop takes effect.
 * Each was correct, and each cost vertical space permanently on a surface
 * where the operator is usually scrolling to reach a field. A section's
 * explanation now hangs off its header as the REQ-UI-133-mandated
 * :func:`DescriptionTooltip`: there when wanted, gone when not, and reachable
 * by pointer, keyboard and touch rather than hover alone.
 *
 * ``help`` is a **string**, not a ``ReactNode`` like ``action``. The tooltip
 * primitive renders its description as React text children and never as
 * markup — the neighbouring metric-catalog descriptions are
 * deployment-configured, and a config file reaching a template context would
 * be a stored-injection surface on an authenticated page. Accepting a node
 * here would reopen that door for whichever caller first passed JSX.
 *
 * Cross-references:
 *   - ADR-024 §4 (one pane, sticky headers, no sub-tabs).
 *   - REQ-UI-059 (labelled sections, keyboard-reachable).
 *   - REQ-UI-133 (the single shared tooltip primitive) / REQ-STATUS-056.
 */
import type { ReactNode } from "react";
import { DescriptionTooltip } from "@/components/ui/tooltip";

/** Props for {@link PeerSection}. */
export interface PeerSectionProps {
  /** Scopes the aria labelling + the section test id. */
  id: string;
  title: string;
  /** Optional control rendered at the right of the sticky header. */
  action?: ReactNode;
  /**
   * How this section's controls behave, disclosed from a ``?`` beside the
   * title. Omit it and no trigger renders at all — a focusable control with
   * nothing to announce is worse than none (REQ-STATUS-056 Verification).
   */
  help?: string;
  children: ReactNode;
}

/** Render one titled section of the management surface. */
export function PeerSection({
  id,
  title,
  action,
  help,
  children,
}: PeerSectionProps): React.ReactElement {
  return (
    <section aria-labelledby={`peer-${id}-heading`} data-testid={`peer-section-${id}`}>
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-bg-surface px-4 py-1.5">
        <h4
          id={`peer-${id}-heading`}
          className="text-meta uppercase tracking-wide text-text-secondary"
        >
          {title}
        </h4>
        {help !== undefined && (
          <DescriptionTooltip
            testId={`peer-section-${id}`}
            label={`the ${title} section`}
            description={help}
          />
        )}
        {action && <div className="ml-auto">{action}</div>}
      </div>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}
